import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { AutoSizer, Grid } from 'react-virtualized';
import { useMediaQuery } from 'react-responsive';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';

import SlideThumbnailSortable, { SlideThumbnail } from '../SlideThumbnail';
import { breakpointDown } from '../../../../../../../../../services/responsive/responsive';

const propTypes = {
  slideshowVideos: PropTypes.arrayOf(
    PropTypes.shape({
      photo: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
  ).isRequired,
  currentVideoId: PropTypes.string,
  onThumbnailClick: PropTypes.func.isRequired,
  onMoveVideo: PropTypes.func.isRequired,
  onDeleteVideo: PropTypes.func.isRequired,
  loadingMore: PropTypes.bool,
  onLoadMore: PropTypes.func.isRequired,
  onReorderVideos: PropTypes.func.isRequired,
  isPlaying: PropTypes.bool.isRequired,
  hasUnprocessedVideos: PropTypes.bool.isRequired,
  className: PropTypes.string,
};
const SlidesSection = ({
  slideshowVideos,
  videosTotal,
  videosOffset,
  currentVideoId,
  onThumbnailClick,
  onMoveVideo,
  onDeleteVideo,
  loadingMore,
  onLoadMore,
  onReorderVideos,
  isPlaying,
  hasUnprocessedVideos,
  className,
}) => {
  const isMobile = useMediaQuery(breakpointDown('sm'));
  const [draggingVideo, setDraggingVideo] = useState(null);

  const currentSlideIndex = slideshowVideos.findIndex(
    ({ video }) => video?.id === currentVideoId,
  );

  // Drag and Drop Context Related

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      delay: 150,
      tolerance: 5,
    },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 250,
      tolerance: 5,
    },
  });

  const onDragEnd = ({ active, over }) => {
    if (!active || !over) {
      return;
    }

    if (active.id !== over.id) {
      const oldIndex = slideshowVideos.findIndex(
        ({ video }) => video.id === active.id,
      );
      const newIndex = slideshowVideos.findIndex(
        ({ video }) => video.id === over.id,
      );
      const reorderedSlides = arrayMove(slideshowVideos, oldIndex, newIndex);
      onReorderVideos(reorderedSlides);
    }
  };

  const onDragStart = ({ active }) => {
    setDraggingVideo(active.data.current.video);
  };

  // --

  const onSectionRendered = ({
    columnOverscanStopIndex,
    rowOverscanStopIndex,
  }) => {
    if (!onLoadMore || loadingMore) {
      return;
    }

    if (slideshowVideos.length === videosTotal) {
      return;
    }

    if (
      isMobile &&
      (videosOffset >= slideshowVideos.length ||
        columnOverscanStopIndex + 1 + videosOffset >= slideshowVideos.length)
    ) {
      onLoadMore();
    }

    if (
      videosOffset >= slideshowVideos.length ||
      rowOverscanStopIndex + 1 + videosOffset >= slideshowVideos.length
    ) {
      onLoadMore();
    }
  };

  const cellRenderer = ({ columnIndex, rowIndex, key, style }) => {
    const slideIndex = isMobile ? columnIndex : rowIndex;
    const { video } = slideshowVideos[slideIndex];
    const isActive = currentVideoId && currentVideoId === video.id;

    return (
      <SlideThumbnailSortable
        key={key}
        style={style}
        video={video}
        isActive={isActive}
        onClick={() => {
          onThumbnailClick({
            videoId: video.id,
            videoIdx: isActive ? undefined : slideIndex,
          });
        }}
        onDelete={
          isPlaying
            ? undefined
            : () =>
                onDeleteVideo({
                  videoIdx: slideIndex,
                  video,
                })
        }
        onMoveUp={
          !onMoveVideo || isPlaying || slideIndex <= 0
            ? undefined
            : () => {
                onMoveVideo({ videoIdx: slideIndex, direction: -1 });
              }
        }
        onMoveDown={
          !onMoveVideo || isPlaying || slideIndex === slideshowVideos.length - 1
            ? undefined
            : () => {
                onMoveVideo({ videoIdx: slideIndex, direction: 1 });
              }
        }
      />
    );
  };

  const classes = classNames({
    'slides-section-wrapper': true,
    [className]: !!className,
  });

  return (
    <DndContext
      onDragEnd={onDragEnd}
      onDragStart={onDragStart}
      sensors={[mouseSensor, touchSensor]}
    >
      <SortableContext
        items={slideshowVideos.map(
          ({ photo, asset_image, account_card }) =>
            photo?.id ?? asset_image?.id ?? account_card?.id,
        )}
        strategy={verticalListSortingStrategy}
      >
        <div className={classes}>
          <div
            className={classNames({
              'w-100': true,
              'd-md-none': videosTotal > 0,
            })}
          >
            <p className="text-uppercase text-extra-bold text-light text-xs mb-0">
              {videosTotal > 0 ? 'Videos' : 'No videos yet'}
            </p>
            {hasUnprocessedVideos && (
              <p className="mb-0 text-primary text-italic text-xs ml-auto">
                Some videos are still being processed&hellip;
              </p>
            )}
            {videosTotal > 0 && (
              <p className="mb-0 text-light text-italic text-xs ml-auto">
                Tap and drag videos to reorder
              </p>
            )}
          </div>
          <div
            className={classNames({
              'w-100': true,
              'h-100': !isMobile,
              'd-none': videosTotal === 0,
            })}
          >
            <div className="slides-section-menu-container">
              <div className="w-100 d-none d-md-block mb-1">
                <p className="text-uppercase text-extra-bold text-light text-xs mb-0">
                  Videos
                </p>
                {hasUnprocessedVideos && (
                  <p className="mb-0 text-primary text-italic text-xs ml-auto">
                    Some videos are still being processed&hellip;
                  </p>
                )}
                {videosTotal > 0 && (
                  <p className="mb-0 text-light text-italic text-xs ml-auto">
                    {isMobile ? 'Tap' : 'Click'} and drag videos to reorder
                  </p>
                )}
              </div>
              <AutoSizer defaultHeight={450}>
                {({ width, height }) => {
                  return (
                    <Grid
                      autoContainerWidth={!isMobile}
                      width={width}
                      height={height}
                      columnWidth={220}
                      rowHeight={isMobile ? 120 : 160}
                      columnCount={isMobile ? slideshowVideos.length : 1}
                      rowCount={isMobile ? 1 : slideshowVideos.length}
                      cellRenderer={cellRenderer}
                      className="slides-section-menu"
                      containerStyle={{ overflow: 'visible' }}
                      scrollToRow={isMobile ? undefined : currentSlideIndex}
                      scrollToColumn={isMobile ? currentSlideIndex : undefined}
                      onSectionRendered={onSectionRendered}
                    />
                  );
                }}
              </AutoSizer>
              <DragOverlay>
                {draggingVideo && (
                  <div className="secondary-photos-view-slides-step-slide-thumbnail-overlay">
                    <SlideThumbnail onClick={() => {}} video={draggingVideo} />
                  </div>
                )}
              </DragOverlay>
            </div>
          </div>
        </div>
      </SortableContext>
    </DndContext>
  );
};

SlidesSection.propTypes = propTypes;

export default SlidesSection;
